import { Skeleton } from "@/components/ui/skeleton";
import { FastSkeleton } from "./FastSkeleton";
import { cn } from "@/lib/utils";

interface OptimizedPageSkeletonProps {
  variant?: 'dashboard' | 'form' | 'table' | 'card-grid' | 'profile';
  showHeader?: boolean;
  showSidebar?: boolean;
  rows?: number;
  animate?: boolean;
  className?: string;
}

export function OptimizedPageSkeleton({ 
  variant = 'dashboard',
  showHeader = true, 
  showSidebar = false, 
  rows = 3,
  animate = true,
  className
}: OptimizedPageSkeletonProps) {
  
  const renderDashboardSkeleton = () => (
    <div className="space-y-6">
      {showHeader && (
        <div className="space-y-3">
          <FastSkeleton className="h-8 w-64" />
          <FastSkeleton className="h-4 w-96" />
        </div>
      )}
      
      {/* Dashboard cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-3 p-4 border border-border rounded-lg">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-4 rounded-full" />
            </div>
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
      
      {/* Charts area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4 p-6 border border-border rounded-lg">
          <Skeleton className="h-6 w-32" />
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-end space-x-1">
                <Skeleton className={`w-8 h-${8 + (i * 2)}`} />
                <Skeleton className={`w-8 h-${6 + (i * 3)}`} />
                <Skeleton className={`w-8 h-${10 + (i * 1)}`} />
              </div>
            ))}
          </div>
        </div>
        
        <div className="space-y-4 p-6 border border-border rounded-lg">
          <Skeleton className="h-6 w-32" />
          <div className="flex items-center justify-center">
            <Skeleton className="h-32 w-32 rounded-full" />
          </div>
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-12" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderFormSkeleton = () => (
    <div className="space-y-6 max-w-2xl">
      {showHeader && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-80" />
        </div>
      )}
      
      <div className="space-y-4">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
        
        <div className="flex space-x-3 pt-4">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-20" />
        </div>
      </div>
    </div>
  );

  const renderTableSkeleton = () => (
    <div className="space-y-4">
      {showHeader && (
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-9 w-32" />
        </div>
      )}
      
      <div className="border border-border rounded-lg">
        {/* Table header */}
        <div className="flex space-x-4 p-4 border-b border-border">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
        </div>
        
        {/* Table rows */}
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex space-x-4 p-4 border-b border-border last:border-b-0">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    </div>
  );

  const renderCardGridSkeleton = () => (
    <div className="space-y-6">
      {showHeader && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-80" />
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: rows * 3 }).map((_, i) => (
          <div key={i} className="p-6 border border-border rounded-lg space-y-4">
            <div className="flex items-start space-x-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48" />
              </div>
            </div>
            <Skeleton className="h-24 w-full rounded" />
            <div className="flex space-x-2">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-24" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderProfileSkeleton = () => (
    <div className="space-y-8 max-w-4xl">
      {/* Profile header */}
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <Skeleton className="h-32 w-32 rounded-full" />
        <div className="text-center sm:text-left space-y-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-64" />
          <div className="flex space-x-2">
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-28" />
          </div>
        </div>
      </div>
      
      {/* Profile sections */}
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="border border-border rounded-lg p-6 space-y-4">
          <Skeleton className="h-6 w-40" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, j) => (
              <div key={j} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderSkeleton = () => {
    switch (variant) {
      case 'dashboard':
        return renderDashboardSkeleton();
      case 'form':
        return renderFormSkeleton();
      case 'table':
        return renderTableSkeleton();
      case 'card-grid':
        return renderCardGridSkeleton();
      case 'profile':
        return renderProfileSkeleton();
      default:
        return renderDashboardSkeleton();
    }
  };

  return (
    <div className={cn(
      "w-full",
      animate && "animate-pulse",
      className
    )}>
      {renderSkeleton()}
    </div>
  );
}